import { useCallback, useMemo } from "react";
import { useQueryCore, invalidateQuery } from "@/hooks/useQueryCore";
import { useDistrictGuard } from "@/hooks/useDistrictGuard";
import * as notesService from "@/lib/notes-service";

const sortNotes = (data) =>
  (data || []).slice().sort((a, b) => {
    const ta = a.updated_locally_at || a.updated_at || a.created_at || "";
    const tb = b.updated_locally_at || b.updated_at || b.created_at || "";
    return tb.localeCompare(ta);
  });

/**
 * Tabs for the Pastoral Knowledge System:
 *   recientes   — all non-archived, sorted newest first (default)
 *   referencia  — is_reference (or legacy pinned) — important/consulted notes
 *   vinculadas  — notes linked to an entity (linked_type !== 'general')
 *   biblioteca  — archived notes (historical record)
 *
 * @param {{ filter?: 'recientes'|'referencia'|'vinculadas'|'biblioteca', linkedType?: string, linkedId?: string }} options
 */
export function useNotes({ filter = "recientes", linkedType = null, linkedId = null } = {}) {
  const { districtId, coordinatorChurchId } = useDistrictGuard();
  const isCoordinator = !!coordinatorChurchId;

  const { data: allNotes, loading, error, refetch, setData } = useQueryCore({
    key: ["notes", districtId, isCoordinator],
    enabled: !!districtId,
    queryFn: () => notesService.loadNotes(districtId, { isCoordinator }),
    sortFn: sortNotes,
  });

  const notes = useMemo(() => {
    if (!allNotes) return [];
    let base;
    switch (filter) {
      case "referencia":
        base = allNotes.filter((n) => (n.is_reference || n.pinned) && !n.archived);
        break;
      case "vinculadas":
        base = allNotes.filter(
          (n) => n.linked_type && n.linked_type !== "general" && !n.archived
        );
        break;
      case "biblioteca":
        base = allNotes.filter((n) => n.archived);
        break;
      case "recientes":
      default:
        base = allNotes.filter((n) => !n.archived);
    }
    // Entity-scoped filter (from "Ver todas" deep-link)
    if (linkedType && linkedId) {
      base = base.filter((n) => n.linked_type === linkedType && n.linked_id === linkedId);
    }
    return base;
  }, [allNotes, filter, linkedType, linkedId]);

  const invalidate = useCallback(() => {
    invalidateQuery(["notes", districtId]);
  }, [districtId]);

  const create = useCallback(
    async (data = {}) => {
      if (!districtId) return null;
      const note = await notesService.createNote(data, districtId);
      setData((prev) => sortNotes([note, ...(prev || [])].filter((n) => !n._deleted)));
      return note;
    },
    [districtId, setData]
  );

  const update = useCallback(
    async (id, updates) => {
      if (!districtId) return;
      const updated = await notesService.updateNote(id, updates, districtId);
      setData((prev) =>
        sortNotes((prev || []).map((n) => (n.id === id ? updated : n)))
      );
      return updated;
    },
    [districtId, setData]
  );

  const remove = useCallback(
    async (id) => {
      if (!districtId) return;
      setData((prev) => (prev || []).filter((n) => n.id !== id));
      await notesService.deleteNote(id, districtId);
    },
    [districtId, setData]
  );

  return {
    notes,
    allNotes,
    loading,
    error,
    refetch,
    create,
    update,
    remove,
    invalidate,
  };
}
